import React, { useRef } from "react";
import AnalysisStatus from "./AnalysisStatus";
import MarkdownViewer from "./MarkdownViewer";
import HtmlViewer from "./HtmlViewer";
import EditMode from "./EditMode";
import SelectionBasedReferenceButton from "../SelectionBasedReferenceButton";
import AIReviewFeedbackRating from "../../../molecules/AIReviewFeedbackRating";
import { AIReviewStatus, Drug, DrugSearchResult, LlmFailureCode } from "../../../../common/models/types";
import { StreamDisconnection } from "../../../../hooks/useAIReviewStream";
import styled from "styled-components";
import { useFeedbackOptional } from "../../../../contexts/FeedbackContext";
import type { HospitalAliasMap } from "../../../../utils/extractDepartment";

const ReviewSectionWrapper = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export type ReferenceMode = 'button' | 'none' | 'include';

interface ReviewSectionProps {
  // 기본 데이터
  reviewId?: number;
  reviewResult?: string;
  reviewResultEdited?: string;
  status?: AIReviewStatus;
  message?: string;
  /** LLM 실패 사유 코드 — 실패 카드가 사유별 문구를 그리는 근거(없으면 현행 표시 유지). */
  llmFailureCode?: LlmFailureCode | null;
  /** 관리자 응답(`analysis`)에만 오는 LLM 실패 원문·기록 시각 — 있으면 실패 카드 하단에 원문 블록을 그린다. */
  llmFailureDetail?: string | null;
  llmFailureDetailAt?: string | null;
  /** 스트림 끊김 상태·재확인 동작. */
  disconnection?: StreamDisconnection;
  promptType?: string;

  // 상태 관리
  isEditMode: boolean;
  editedContent: string;
  showEditedVersion: boolean;

  // 이벤트 핸들러
  onEnterEditMode: () => void;
  onExitEditMode: () => void;
  onContentChange: (content: string) => void;
  onSave: () => void;
  onDelete: () => void;
  onReferenceClick?: (referenceId: string) => void;
  onDrugImageClick?: (itemCode: string) => void;

  // 피드백 관련 (Context에서 상태 가져옴, 탭 전환 콜백만 props)
  onActivateFeedbackTab?: () => void;

  // 기타
  isSaving?: boolean;
  hasEditedVersion?: boolean;
  editorRef?: React.RefObject<any>;
  referenceMode?: ReferenceMode;
  drugsData?: Drug[];
  pillsData?: DrugSearchResult[];
  sectionVisibility?: {
    hidePharmacistReport: boolean;
    hidePatientGuide: boolean;
  };
  // 병원 버튼화(spec Phase 3·4) — 3단·인쇄·팝업·임베드로 흐르는 통로. 전부 optional.
  //   선택 상태는 본문에 표시하지 않으므로 highlighted* 통로 없음(spec 설계 결정 13).
  hospitalAliasMap?: HospitalAliasMap;
  onHospitalClick?: (alias: string) => void;
  // 처방전 지칭 버튼화(spec 설계 결정 11).
  onPrescriptionClick?: (n: number) => void;
  prescriptionGroupCount?: number;
}

const ReviewSection: React.FC<ReviewSectionProps> = ({
  reviewId,
  reviewResult,
  reviewResultEdited,
  status,
  message,
  llmFailureCode,
  llmFailureDetail,
  llmFailureDetailAt,
  disconnection,
  promptType,
  isEditMode,
  editedContent,
  showEditedVersion,
  onEnterEditMode,
  onExitEditMode,
  onContentChange,
  onSave,
  onDelete,
  onReferenceClick,
  onDrugImageClick,
  onActivateFeedbackTab,
  isSaving = false,
  hasEditedVersion = false,
  editorRef,
  referenceMode = 'button',
  drugsData,
  pillsData,
  sectionVisibility,
  hospitalAliasMap,
  onHospitalClick,
  onPrescriptionClick,
  prescriptionGroupCount,
}) => {
  const reviewSectorRef = useRef<HTMLDivElement>(null);

  // Context에서 피드백 상태 가져오기 (Provider 외부에서도 안전하게 사용)
  const feedbackContext = useFeedbackOptional();
  const phase = feedbackContext?.phase ?? "hidden";

  // 편집 모드 진입 시 처리
  // Template에서 이미 적절한 HTML content를 설정하므로 여기서는 단순히 호출만 함
  const handleEnterEditMode = () => {
    onEnterEditMode();
  };

  // 실패 상태인 경우 실패 카드 표시
  if (status === AIReviewStatus.FAILED) {
    return (
      <ReviewSectionWrapper>
        <AnalysisStatus
          status={status}
          message={message}
          llmFailureCode={llmFailureCode}
          llmFailureDetail={llmFailureDetail}
          llmFailureDetailAt={llmFailureDetailAt}
        />
      </ReviewSectionWrapper>
    );
  }

  // 분석 중 상태
  if (!reviewResult && status !== AIReviewStatus.COMPLETED) {
    return (
      <ReviewSectionWrapper>
        <AnalysisStatus status={status} message={message} disconnection={disconnection} />
      </ReviewSectionWrapper>
    );
  }

  // 편집 모드
  if (isEditMode) {
    return (
      <ReviewSectionWrapper>
        <EditMode
          content={editedContent}
          onChange={onContentChange}
          onDelete={onDelete}
          onReferenceClick={onReferenceClick}
          promptType={promptType}
          isSaving={isSaving}
          hasEditedVersion={hasEditedVersion}
          editorRef={editorRef}
          drugsData={drugsData}
          pillsData={pillsData}
        />
        <SelectionBasedReferenceButton
          containerRef={reviewSectorRef}
          isEditMode={true}
        />
      </ReviewSectionWrapper>
    );
  }

  // 뷰어 모드
  const contentToShow = showEditedVersion && reviewResultEdited ? reviewResultEdited : reviewResult;
  const isHtml = showEditedVersion && reviewResultEdited;

  // 피드백 버튼 표시 조건: phase가 hidden이 아니고 편집 모드가 아닐 때
  const showFeedbackButtons =
    !isEditMode &&
    phase !== "hidden" &&
    reviewId;

  return (
    <ReviewSectionWrapper ref={reviewSectorRef}>
      {isHtml ? (
        <HtmlViewer
          content={contentToShow || ''}
          onReferenceClick={onReferenceClick}
          referenceMode={referenceMode}
          drugsData={drugsData}
          pillsData={pillsData}
          onDrugImageClick={onDrugImageClick}
          sectionVisibility={sectionVisibility}
          hospitalAliasMap={hospitalAliasMap}
          onHospitalClick={onHospitalClick}
          onPrescriptionClick={onPrescriptionClick}
          prescriptionGroupCount={prescriptionGroupCount}
        />
      ) : (
        <MarkdownViewer
          content={contentToShow || ''}
          promptType={promptType}
          onReferenceClick={onReferenceClick}
          referenceMode={referenceMode}
          drugsData={drugsData}
          pillsData={pillsData}
          onDrugImageClick={onDrugImageClick}
          sectionVisibility={sectionVisibility}
          hospitalAliasMap={hospitalAliasMap}
          onHospitalClick={onHospitalClick}
          onPrescriptionClick={onPrescriptionClick}
          prescriptionGroupCount={prescriptionGroupCount}
        />
      )}

      {/* 피드백 별점 - DOM 추출 대상 아님 */}
      {showFeedbackButtons && (
        <AIReviewFeedbackRating
          reviewId={reviewId}
          onActivateFeedbackTab={onActivateFeedbackTab}
        />
      )}
    </ReviewSectionWrapper>
  );
};

export default ReviewSection;